import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { teamService, ITeamDetail } from './service/team.service';
import { reviewService } from '../review/service/review.service';
import useAuth from '../auth/feature/auth';
import { useTeamSocket } from './hooks/use-team-socket';
import { DiscordPill, WebhookPill } from '../common/components/discord-pill.component';
import { InviteModal } from '../common/components/invite-modal.component';
import { UpgradeModal } from '../common/components/upgrade-modal.component';
import { CreateQueueModal, CreateQueueFormData } from '../common/components/create-queue-modal.component';

export const TeamDashboard = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  useTeamSocket(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showLeaveTeamModal, setShowLeaveTeamModal] = React.useState(false);
  const [editingName, setEditingName] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');

  const { mutate: leaveTeam, isPending: leaveTeamPending } = useMutation({
    mutationFn: () => teamService.leaveTeam(+id!),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['my-teams'] });
      queryClient.removeQueries({ queryKey: ['team-active-reviews'] });
      toast.success(t('teams.leftTeam'));
      navigate('/teams');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      const detail = Array.isArray(msg) ? msg[0] : msg;
      toast.error(detail || t('teams.leaveFailed'));
    }
  });

  const { mutate: renameTeam, isPending: renamePending } = useMutation({
    mutationFn: (name: string) => teamService.updateTeam(+id!, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      queryClient.invalidateQueries({ queryKey: ['my-teams'] });
      setEditingName(false);
      toast.success(t('teams.teamRenamed'));
    },
    onError: () => toast.error(t('teams.renameFailed'))
  });

  const { data: team, isLoading, error: teamError } = useQuery<ITeamDetail | void>({
    queryKey: ['team', id],
    queryFn: () => teamService.getTeam(+id!),
    enabled: !!id,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403 || error?.response?.status === 404) return false;
      return failureCount < 3;
    }
  });

  // Redirect away if user was removed from this team
  React.useEffect(() => {
    const status = (teamError as any)?.response?.status;
    if (status === 403 || status === 404) {
      navigate('/teams');
    }
  }, [teamError, navigate]);

  const { data: reviews } = useQuery({
    queryKey: ['team-reviews', id],
    queryFn: () => teamService.getTeamReviews(+id!, true),
    enabled: !!id,
    refetchInterval: false,
    refetchOnMount: 'always',
    staleTime: 0
  });

  const { mutate: createReview, isPending: createPending } = useMutation({
    mutationFn: (data: CreateQueueFormData) =>
      reviewService.createReview(data.title, data.meetingUrl, [+id!], data.password),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['team-reviews', id] });
        queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
        setShowCreateModal(false);
        navigate(`/review/${data.slug}/admin`);
      }
    },
    onError: (err: any) => {
      const messages = err.response?.data?.message;
      const detail = Array.isArray(messages) ? messages[0] : messages;
      if (err.response?.status === 403 && detail) {
        setUpgradeMessage(detail);
      } else {
        toast.error(detail || t('dashboard.createReviewFailed'));
      }
    }
  });

  const myRole = team?.members?.find((m) => m.userId === user?.id)?.role || 'member';

  if (isLoading || !team) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] dark:border-[#C8C8CB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-[700px] mx-auto py-[32px] md:py-[48px] px-[16px]">
      <Link to="/teams" className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white transition-colors mb-[8px]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        {t('header.teams')}
      </Link>
      <div className="mb-[16px]">
        {editingName ? (
          <div className="flex gap-[8px] mb-[12px]">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="flex-1 h-[44px] px-[16px] text-[20px] font-display font-bold text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && nameInput.trim()) renameTeam(nameInput.trim()); if (e.key === 'Escape') setEditingName(false); }}
            />
            <button
              type="button"
              onClick={() => { if (nameInput.trim()) renameTeam(nameInput.trim()); }}
              disabled={renamePending || !nameInput.trim()}
              className="h-[44px] px-[16px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => setEditingName(false)}
              className="h-[44px] px-[16px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-[10px] mb-[12px]">
            <h1 className="font-display text-[24px] md:text-[28px] font-bold text-base-black dark:text-white">
              {team.name}
            </h1>
            {myRole === 'admin' && (
              <button
                type="button"
                onClick={() => { setNameInput(team.name); setEditingName(true); }}
                className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0"
                title={t('teams.renameTeam')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="flex gap-[8px] flex-wrap">
          {myRole === 'admin' && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="h-[36px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all flex items-center gap-[6px]"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              {t('teams.inviteMembers')}
            </button>
          )}
          {myRole === 'admin' && (
            <button
              type="button"
              onClick={() => navigate(`/team/${id}/admin`)}
              className="h-[36px] px-[14px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all"
            >
              {t('teams.manageTeam')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowLeaveTeamModal(true)}
            className="h-[36px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] hover:text-base-black dark:hover:text-white transition-all flex items-center gap-[6px]"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('teams.leaveTeam')}
          </button>
        </div>
      </div>

      <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] mb-[24px]">
        {team.members.length} member{team.members.length !== 1 ? 's' : ''} &middot;{' '}
        {team.reviewCount} queue{team.reviewCount !== 1 ? 's' : ''}
      </p>

      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)]">

      <div className="mb-[12px]">
        <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[12px]">
          {t('teams.activeReviews')}
        </h2>
        {myRole === 'admin' && (
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="h-[36px] px-[16px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all"
          >
            {t('dashboard.createReview')}
          </button>
        )}
      </div>

      {(reviews as any[])?.length > 0 ? (
        <div className="flex flex-col gap-[8px]">
          {(reviews as any[]).map((review: any) => (
            <div
              key={review.id}
              onClick={() => navigate(
                review.adminId === user?.id
                  ? `/review/${review.slug}/admin`
                  : `/review/${review.slug}`
              )}
              className="flex flex-col gap-[4px] p-[12px_16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-[#F5F5F6] dark:bg-[#28282B] hover:shadow-[0_2px_10px_rgba(42,60,68,0.08)] transition-all cursor-pointer md:flex-row md:items-center md:justify-between"
            >
              <div className="min-w-0">
                <p className="font-body text-[15px] font-semibold text-[#111827] dark:text-white truncate">
                  {review.title}
                </p>
              </div>
              <div className="flex items-center gap-[6px] flex-wrap flex-shrink-0">
                <span className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3]">
                  {review._count?.participants || 0} {t('common.participants')}
                </span>
                {review.discordWebhookUrl && (
                  <DiscordPill channelUrl={review.discordChannelUrl} variant="icon" />
                )}
                {review.slackWebhookUrl && (
                  <WebhookPill provider="slack" channelUrl={review.slackChannelUrl} variant="icon" />
                )}
                {review.isActive && (
                  <span className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[32px] text-[11px] font-semibold ${
                    review.meetingStarted
                      ? 'bg-green-500 text-green-900 border border-green-900/30 animate-pulseBorder'
                      : 'bg-[#DFDFE2] text-[#58585C] border border-[rgba(0,0,0,0.10)]'
                  }`}>
                    <span className={`w-[6px] h-[6px] rounded-full flex-shrink-0 ${
                      review.meetingStarted ? 'bg-green-900 animate-pulseStrong' : 'bg-[#97979B]'
                    }`} />
                    {review.meetingStarted ? t('meetingStatus.inProgress') : t('meetingStatus.waiting')}
                  </span>
                )}
                <span className="font-mono text-[12px] text-gray-700 dark:text-[#6B6B6F]">
                  /{review.slug}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] text-center py-[24px]">
          {t('teams.noActiveReviews')}
        </p>
      )}

      </div>

      {/* Members list — visible to everyone */}
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] mt-[16px]">
        <p className="font-body text-[12px] font-semibold text-gray-900 dark:text-[#6B6B6F] uppercase tracking-wider mb-[8px]">
          {t('teams.membersCount', { count: team.members.length })}
        </p>
        <div className="flex flex-col gap-[6px]">
          {team.members.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-[12px] p-[12px_16px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-white dark:bg-[#28282B] shadow-[0_1px_3px_rgba(0,0,0,0.07)]"
            >
              <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] text-[12px] font-semibold flex-shrink-0">
                {(member.displayName || member.email).slice(0, 2).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-body text-[14px] font-semibold text-[#111827] dark:text-white">
                  {member.displayName || member.email.split('@')[0]}
                </p>
                <p className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3] break-all">
                  {member.email}
                </p>
              </div>
              <span className={`text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] uppercase flex-shrink-0 ${
                member.role === 'admin' ? 'bg-[#1D1D1E] text-white' : 'bg-[#EFEFF1] dark:bg-[#333336] text-[#4A4A4C] dark:text-[#A0A0A3]'
              }`}>
                {member.role === 'admin' ? t('common.admin') : t('common.memberRole')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {team && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          title={t('inviteModal.inviteToTeam')}
          copyLabel={t('inviteModal.teamInviteLink')}
          copyUrl={`${window.location.origin}/team/join/${team.inviteCode}`}
          isPro={user?.isPro || false}
          onUpgrade={() => setUpgradeMessage(t('review.inviteTrackerProRequired'))}
          onSendInvites={async (emails) => {
            await teamService.sendInvites(team.id, emails);
            toast.success(`Invited ${emails.length} ${emails.length === 1 ? 'person' : 'people'} to ${team.name}`);
          }}
        />
      )}

      <CreateQueueModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={(data) => createReview(data)}
        isPending={createPending}
        hasPro={!!(user?.isPro || user?.isProGifted || user?.subscriptionTier === 'trial')}
        onUpgrade={(msg) => setUpgradeMessage(msg)}
      />

      {upgradeMessage && (
        <UpgradeModal message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />
      )}

      {showLeaveTeamModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLeaveTeamModal(false); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black dark:text-white mb-[4px]">
                {t('teams.leaveTeam')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
                {t('teams.leaveTeamConfirmMessage')}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => { leaveTeam(); setShowLeaveTeamModal(false); }}
                disabled={leaveTeamPending}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all disabled:opacity-50"
              >
                {leaveTeamPending ? t('common.leaving') : t('teams.confirmLeave')}
              </button>
              <button
                type="button"
                onClick={() => setShowLeaveTeamModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
